import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  LayoutDashboard, FileText, Columns3, Users, Settings, LogOut,
  PlusCircle, Menu, X, ChevronDown, Zap
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../ui/dropdown-menu';

const NAV_ITEMS = [
  { to: '/', icon: LayoutDashboard, label: 'Tableau de bord' },
  { to: '/leads', icon: FileText, label: 'Leads' },
  { to: '/kanban', icon: Columns3, label: 'Kanban' },
  { to: '/partners', icon: Users, label: 'Partenaires' },
  { to: '/settings', icon: Settings, label: 'Parametres', internalOnly: true },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors duration-150 ${
      isActive
        ? 'bg-[hsl(220,30%,15%)] text-white border-l-2 border-[#397DFF]'
        : 'text-slate-400 hover:text-white hover:bg-[hsl(220,30%,12%)]'
    }`;

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="app-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-[240px] bg-[#0B1120] flex flex-col transition-transform duration-200 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      }`} data-testid="sidebar">
        {/* Logo */}
        <div className="flex items-center gap-2 px-5 py-5 border-b border-[hsl(220,20%,18%)]">
          <Zap className="h-6 w-6 text-[#397DFF]" strokeWidth={2} />
          <div>
            <p className="font-heading font-bold text-white text-sm leading-tight">Place des Energies</p>
            <p className="text-[10px] text-slate-500 font-medium tracking-wider uppercase">Efficacite Energetique</p>
          </div>
          <button className="lg:hidden ml-auto text-slate-400" onClick={() => setSidebarOpen(false)}>
            <X size={18} />
          </button>
        </div>

        {/* New Lead button */}
        <div className="px-4 pt-5 pb-2">
          <Button
            data-testid="new-lead-btn"
            className="w-full bg-[#397DFF] hover:bg-[#2563EB] text-white font-semibold h-10 gap-2"
            onClick={() => { navigate('/leads/new'); setSidebarOpen(false); }}
          >
            <PlusCircle size={16} />
            Nouveau lead
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 space-y-0.5" data-testid="sidebar-nav">
          {NAV_ITEMS.filter(item => !item.internalOnly || user?.role === 'equipe_interne').map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={navLinkClass}
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <item.icon size={18} strokeWidth={1.5} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div className="px-4 py-4 border-t border-[hsl(220,20%,18%)]">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 w-full text-left text-sm" data-testid="user-menu-trigger">
                <div className="w-8 h-8 bg-[#397DFF] flex items-center justify-center text-white font-bold text-xs flex-shrink-0" style={{borderRadius:'50%'}}>
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{user?.name || 'Utilisateur'}</p>
                  <p className="text-slate-500 text-[11px] truncate">{user?.role || ''}</p>
                </div>
                <ChevronDown size={14} className="text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut size={14} className="mr-2" />
                Deconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar (mobile) */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3 bg-white border-b">
          <button onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
            <Menu size={22} />
          </button>
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-[#397DFF]" strokeWidth={2} />
            <span className="font-heading font-bold text-sm">PDE - EE</span>
          </div>
          <div className="w-8" />
        </header>

        {/* Content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
